import { useAppSelector } from '../../../app/hooks';
import {
  ChartDropdownDimensionEnum,
  CubeMeasureEnum,
  FeatureEnum,
  selectCurrChartDimension,
} from '../../../services/data/impl/cube/cubeSlice';
import {
  isBlueberryFeature,
  isBlueberryFlowerFeature,
} from '../../../services/data/impl/cube/cubeUtils';
import { useTranslation } from '../../../services/translation/hooks/useTranslation';
import { MeasurementTypes, useUnitSymbol } from '../../commonUtils';
import {
  selectCurrBlueberryClass,
  selectCurrFeature,
  selectCurrMeasure,
} from '../../select/selectSlice';

export const useChartFeatureMeasureLabels = () => {
  const currChartDimension = useAppSelector(selectCurrChartDimension);
  const currMeasure = useAppSelector(selectCurrMeasure);
  const currFeature = useAppSelector(selectCurrFeature);
  const currBlueberryClass = useAppSelector(selectCurrBlueberryClass);

  const { t } = useTranslation();

  const meterOrFeetUnitSymbol = useUnitSymbol(MeasurementTypes.meterOrFeet);
  const millimeterUnitSymbol = useUnitSymbol(MeasurementTypes.millimeterOrInch);

  let yLabel = '';
  let xLabel = '';

  if (currChartDimension === ChartDropdownDimensionEnum.distribution) {
    yLabel = t('Frequency');

    if (currMeasure === CubeMeasureEnum.DetectionFactAvgLength) {
      if (currFeature === FeatureEnum.berries) {
        xLabel = `${t('Average')} ${t('Diameter')} (${millimeterUnitSymbol})`;
      } else if (
        [FeatureEnum.shoot, FeatureEnum.earlyShoot].includes(currFeature)
      ) {
        xLabel = `${t('Length')} (${millimeterUnitSymbol})`;
      } else {
        xLabel = `${t(currMeasure)} (${millimeterUnitSymbol})`;
      }
    } else {
      if (isBlueberryFeature(currFeature)) {
        xLabel =
          currBlueberryClass === null
            ? 'Flowers and '
            : currBlueberryClass === FeatureEnum.blueberry
            ? ''
            : `${t(currBlueberryClass || FeatureEnum.blueberry)} `;
        xLabel += `${t('Blueberries')} / ${meterOrFeetUnitSymbol}`;

        if (isBlueberryFlowerFeature(currBlueberryClass)) {
          xLabel = `${t('Flowers')} / ${meterOrFeetUnitSymbol}`;
        }
      } else {
        xLabel = `${t(currFeature)} ${t('per')} ${t(meterOrFeetUnitSymbol)}`;
      }
    }
  } else if (
    currChartDimension === ChartDropdownDimensionEnum.colorDistribution
  ) {
    yLabel = t('Frequency');
    if (isBlueberryFeature(currFeature)) {
      if (!currBlueberryClass) {
        xLabel = `${t('Ripeness Stage')}`;
      } else if (isBlueberryFeature(currBlueberryClass)) {
        xLabel = `${t('Color')}`;
      } else if (isBlueberryFlowerFeature(currBlueberryClass)) {
        xLabel = `${t('Flower Stage')}`;
      }
      yLabel = `${t('Estimated Count')} ${t('per')} ${t('Stage')}`;
    } else {
      xLabel = `${t(currMeasure)} ${t('(Click to hide / show)')}`;
    }
  } else if (currChartDimension === ChartDropdownDimensionEnum.perRowside) {
    xLabel = `${t(currFeature)} - ${t('Rowside')}`;
    yLabel = `${t('Total')} ${t(currFeature)}`;

    if (isBlueberryFeature(currFeature)) {
      if (!currBlueberryClass) {
        xLabel = `${t('Berries and Flowers')} - ${t('Rowside')}`;
        yLabel = `${t('Total')} ${t('Berries and Flowers')}`;
      } else if (isBlueberryFlowerFeature(currBlueberryClass)) {
        if (currBlueberryClass === FeatureEnum.flower) {
          xLabel = `${t('Flowers')} - ${t('Rowside')}`;
          yLabel = `${t('Total')} ${t('Flowers')}`;
        } else {
          xLabel = `${t(currBlueberryClass)} ${t('Flowers')} - ${t('Rowside')}`;
          yLabel = `${t('Total')} ${t(currBlueberryClass)} ${t('Flowers')}`;
        }
      } else {
        if (currBlueberryClass === FeatureEnum.blueberry) {
          xLabel = `${t('Berries')} - ${t('Rowside')}`;
          yLabel = `${t('Total')} ${t('Berries')}`;
        } else {
          xLabel = `${t(currBlueberryClass)} ${t('Berries')} - ${t('Rowside')}`;
          yLabel = `${t('Total')} ${t(currBlueberryClass)} ${t('Berries')}`;
        }
      }
    }
  }

  return {
    yLabel,
    xLabel,
  };
};
